import React, { DragEvent, ReactNode, useCallback, useEffect } from 'react';
import { Box, SxProps, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { color } from '@/colors';
import { EllipsisText } from '@/UI/EllipsisText/EllipsisText';
import { Flex, FlexColCenHorVer } from '@/UI/styles';
import { I18N_KEYS } from '@/language/keys';
import { ModelItem } from '@/state/nodes/nodes.types';
import { Tag } from '@/UI/Tag/Tag';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { PencilLineIcon } from '@/UI/Icons/PencilLineIcon';
import { TrashIcon } from '@/UI/Icons/TrashIcon';
import { NodeDetails } from './NodeDetails';
import modelIconMap from './NodeIconMap';
import type { SubflowTemplateResponseDto } from '@/types/api/subflowTemplate';

type LeftPanelMenuItemProps = {
  item: ModelItem;
  onDragEnd: (event: DragEvent<HTMLDivElement>) => void;
  onDragStart: (event: DragEvent<HTMLDivElement>, item: ModelItem) => void;
  disabled?: boolean;
  selected?: boolean;
  shouldDisplayEmptyOutput?: boolean;
  isEditMode?: boolean;
  onEdit?: (item: ModelItem) => void;
  onDelete?: (item: ModelItem) => void;
  canModify?: boolean;
  template?: SubflowTemplateResponseDto;
};

export function LeftPanelMenuItem({
  item,
  onDragStart,
  onDragEnd,
  disabled = false,
  selected = false,
  shouldDisplayEmptyOutput = false,
  isEditMode = false,
  onEdit,
  onDelete,
  canModify = true,
  template,
}: LeftPanelMenuItemProps) {
  const { t } = useTranslation();

  const handleDragStart = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (!disabled) {
        onDragStart(event, item);
      }
    },
    [disabled, item, onDragStart],
  );

  const handleDragEnd = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (!disabled) {
        onDragEnd(event);
      }
    },
    [disabled, onDragEnd],
  );

  const getStyle = (): SxProps => {
    if (disabled) {
      return {
        borderRadius: 1,
        cursor: 'not-allowed',
        opacity: 0.25,
        pointerEvents: 'none',
      };
    }
    if (selected) {
      return {
        borderRadius: 1,
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        '&:hover': {
          backgroundColor: color.Black84,
        },
      };
    }
    return {
      borderRadius: 1,
      '&:hover': {
        backgroundColor: color.Black84,
      },
    };
  };

  const renderIcon = () => {
    if (!item.icon) {
      return null;
    }

    const iconDisplay: string | ReactNode = modelIconMap[item.icon];
    if (typeof iconDisplay === 'string') {
      return <img src={iconDisplay} width="20px" />;
    }

    return (
      React.isValidElement(iconDisplay) &&
      React.cloneElement(iconDisplay, { width: 20, height: 20 } as React.SVGProps<SVGSVGElement>)
    );
  };

  useEffect(() => {
    if (selected) {
      const menuItem = document.getElementById(`left-panel-menu-item-${item.id}`);
      if (menuItem) {
        menuItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [selected, item.id]);

  const tooltipTitle =
    isEditMode && !canModify
      ? t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.WORKSPACE_ASSETS.NOT_OWNER_TOOLTIP)
      : (item?.description || template) && (
          <NodeDetails item={item} shouldDisplayEmptyOutput={shouldDisplayEmptyOutput} template={template} />
        );

  return (
    <Tooltip
      title={tooltipTitle}
      placement="right-start"
      enterDelay={800}
      enterNextDelay={800}
      // mui tooltip max width is 300px
      slotProps={{ tooltip: { sx: { maxWidth: 320, p: 0, borderRadius: 2 } } }}
    >
      <Box
        id={`left-panel-menu-item-${item.id}`}
        sx={getStyle()}
        onDragStart={isEditMode ? undefined : handleDragStart}
        onDragEnd={isEditMode ? undefined : handleDragEnd}
        draggable={!disabled && !isEditMode}
      >
        <FlexColCenHorVer
          className={selected ? 'model-menu-item-glow' : ''}
          sx={{
            position: 'relative',
            border: '1px solid',
            borderColor: selected ? color.Black08 : color.White16_T,
            borderRadius: 1,
            py: 2,
            px: 0.5,
            cursor: isEditMode ? 'default' : 'grab',
            height: '100px',
            gap: 1,
          }}
        >
          {isEditMode && (
            <Flex
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                gap: 0.25,
                zIndex: 1,
              }}
            >
              <AppIconButton
                width={20}
                height={20}
                disabled={!canModify}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit?.(item);
                }}
              >
                <PencilLineIcon width={14} height={14} />
              </AppIconButton>
              <AppIconButton
                width={20}
                height={20}
                disabled={!canModify}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete?.(item);
                }}
              >
                <TrashIcon width={14} height={14} />
              </AppIconButton>
            </Flex>
          )}
          {!isEditMode && item.isNew && (
            <Tag
              sx={{ position: 'absolute', top: 4, right: 4, px: 0.5 }}
              text={t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.NEW_INDICATOR)}
              textColor={color.Yellow100}
              bgColor={color.Yellow16_T}
            />
          )}
          {renderIcon()}
          <EllipsisText
            variant="body-sm-rg"
            sx={{ textAlign: 'center', width: '100%' }}
            isMultiLine
            linesNumber={2}
            disableHoverListener={!!item?.description || !!template}
          >
            {item.displayName}
          </EllipsisText>
        </FlexColCenHorVer>
      </Box>
    </Tooltip>
  );
}
